import { useState } from 'react';
import { makeStyles, mergeClasses, Text, tokens, Tooltip } from '@fluentui/react-components';
import {
    ClipboardTask20Filled,
    ClipboardTask20Regular,
    DismissFilled,
    DismissRegular,
    EditFilled,
    EditRegular,
    Money20Filled,
    bundleIcon,
} from '@fluentui/react-icons';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomTableCellLayout,
} from '../../controls/CustomDataTable/CustomDataTable';
import { TaskTemplate } from '../../../data/types';
import { useAccountState } from '../../../redux/reducers/accountReducer';
import { useAccess } from '../../../auth/useAccess';
import { ConfirmationDialog } from '../../dialogs/ConfirmationDialog';
import { Column, Row } from '../../common';
import { AddTaskTemplateDialog } from '../../dialogs/AddTaskTemplateDialog';

const useStyles = makeStyles({
    taskText: {
        padding: '0px',
        margin: '0',
        overflow: 'hidden',
        width: 'inherit',
        display: 'block',
        overflowWrap: 'break-word',
        maxWidth: '100%',
    },
    taskTextWordBreak: {
        wordBreak: 'break-all',
    },
    completeBy: {
        color: tokens.colorBrandForeground1,
        pointerEvents: 'none',
        userSelect: 'none',
    },
    completeByOverdue: {
        color: tokens.colorStatusDangerForeground1,
        fontWeight: tokens.fontWeightBold,
    },
});

enum ColumnName {
    task = 'task',
    billable = 'billable',
}

type TaskTemplateTableProps = {
    height?: string;
    caseId?: string;
    taskTemplates?: TaskTemplate[];
    onDelete?: (taskTemplate: TaskTemplate) => Promise<void>;
    onSubmit?: (taskTemplate: TaskTemplate) => Promise<void>;
};

export const TaskTemplateTable = (props: TaskTemplateTableProps) => {
    const { height, caseId, taskTemplates, onDelete, onSubmit } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);

    //State
    const [actionTaskTemplate, setActionTaskTemplate] = useState<TaskTemplate>();
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [editOpen, setEditOpen] = useState<boolean>(false);

    //Styles
    const styles = useStyles();

    //UI Events
    const onConfirmDeleteRow = (taskTemplate: TaskTemplate) => () => {
        if (onDelete) onDelete(taskTemplate);
    };
    const onDeleteConfirmOpenChange = (...args: any[]) => {
        const maybeOpen = typeof args[0] === 'boolean' ? args[0] : (args?.[1]?.open ?? args?.[0]);
        if (typeof maybeOpen === 'boolean') {
            setDelConfirmOpen(maybeOpen);
            setActionTaskTemplate(undefined);
        }
    };
    const onEditOpenChange = (...args: any[]) => {
        const maybeOpen = typeof args[0] === 'boolean' ? args[0] : (args?.[1]?.open ?? args?.[0]);
        if (typeof maybeOpen === 'boolean') {
            setEditOpen(maybeOpen);
        }
    };
    const onConfirmDeleteActionTemplate = () => {
        if (actionTaskTemplate && onDelete) onDelete(actionTaskTemplate);
    };

    //Icons
    const DismissBundle = bundleIcon(DismissFilled, DismissRegular);
    const EditBundle = bundleIcon(EditFilled, EditRegular);

    //Columns and Actions
    const columns: CustomDataTableColumn<TaskTemplate>[] = [
        {
            id: ColumnName.task,
            header: 'Task',
            renderCell: (task) => {
                const hasLongWord = (text: string, length = 25): boolean => {
                    return text.split(/\s+/).some((word) => word.length > length);
                };
                const taskTextClasses = mergeClasses(
                    styles.taskText,
                    hasLongWord(task?.text as string) && styles.taskTextWordBreak,
                );

                return (
                    <>
                        <Row gap='small'>
                            <Column flex='0' vAlign='top' gap='small'>
                                {!!task?.billable ? (
                                    <Tooltip content='Billable Task' relationship='label'>
                                        <ClipboardTask20Filled color={tokens.colorBrandStroke1} />
                                    </Tooltip>
                                ) : (
                                    <Tooltip content='Non-billable Task' relationship='label'>
                                        <ClipboardTask20Regular />
                                    </Tooltip>
                                )}
                            </Column>
                            <Column flex='3' vAlign='top' gap='small'>
                                <Text size={300} className={taskTextClasses}>
                                    {task?.title || task?.text}
                                </Text>
                            </Column>
                        </Row>
                    </>
                );
            },
            pointerEvents: true,
            defaultWidth: 350,
            minWidth: 150,
        },
        {
            id: ColumnName.billable,
            compare: (a, b) => {
                return Number(a.billable) - Number(b.billable);
            },
            header: 'Billable?',
            renderCell: (task) => {
                return (
                    <>
                        {task?.billable ? (
                            <Row noGap>
                                <Column width='auto'>
                                    <Money20Filled color='green' />
                                </Column>
                                <Column align='center' padding='pl-1'>
                                    <Text weight='semibold'>Billable</Text>
                                </Column>
                            </Row>
                        ) : (
                            <Text>Non-Billable</Text>
                        )}
                    </>
                );
            },
            defaultWidth: 125,
            minWidth: 100,
        },
    ];

    const actions: CustomDataTableAction<TaskTemplate>[] = [
        {
            id: 'edit',
            ariaLabel: 'Edit',
            canAccess: () => access.cases.canEditCaseEngagement(caseId) || false,
            icon: <EditBundle />,
            label: 'Edit',
            tooltip: 'Edit',
            singleSelectItemOnClick: true,
            onMenuClick: (taskTemplate: TaskTemplate, e?: React.MouseEvent) => {
                e?.stopPropagation?.();
                setActionTaskTemplate(taskTemplate);
                setEditOpen(true);
            },
            renderDialog: (taskTemplate: TaskTemplate, children: any) => {
                return (
                    <AddTaskTemplateDialog taskToEdit={taskTemplate} onSubmit={onSubmit}>
                        {children}
                    </AddTaskTemplateDialog>
                );
            },
        },
        {
            id: 'remove',
            ariaLabel: 'Remove',
            canAccess: () => access.cases.canEditCaseEngagement(caseId) || false,
            icon: <DismissBundle />,
            label: 'Remove',
            tooltip: 'Remove',
            singleSelectItemOnClick: true,
            onMenuClick: (taskTemplate: TaskTemplate, e?: React.MouseEvent) => {
                e?.stopPropagation?.();
                setActionTaskTemplate(taskTemplate);
                setDelConfirmOpen(true);
            },
            renderDialog: (taskTemplate: TaskTemplate, children: any) => {
                return (
                    <ConfirmationDialog
                        title='Confirm Removal'
                        icon={<DismissRegular />}
                        text={`Are you sure you want to remove this task?`}
                        onConfirm={onConfirmDeleteRow(taskTemplate)}
                    >
                        {children}
                    </ConfirmationDialog>
                );
            },
        },
    ];

    return (
        <>
            <CustomDataTable<TaskTemplate>
                items={taskTemplates || []}
                rowId='id'
                bodyHeight={`calc(${height} - 4rem)`}
                select={false}
                columns={columns}
                actions={actions}
            />
            <ConfirmationDialog
                title='Confirm Removal'
                icon={<DismissRegular />}
                text={'Are you sure you want to remove this task?'}
                open={delConfirmOpen}
                onOpenChange={onDeleteConfirmOpenChange}
                onConfirm={onConfirmDeleteActionTemplate}
            />
            <AddTaskTemplateDialog
                taskToEdit={actionTaskTemplate}
                onSubmit={onSubmit}
                open={editOpen}
                onOpenChange={onEditOpenChange}
            />
        </>
    );
};
